/**
 * Phone Number Formatting Utility
 * Formats phone numbers to display country code separated by dash
 * Example: +916789234595 -> +91-6789234595
 * Works with all country codes (1-digit, 2-digit, 3-digit)
 */

/**
 * Format phone number to display country code separated by dash
 * @param {string} phoneNumber - Phone number in any format (e.g., +916789234595)
 * @returns {string} - Formatted phone number (e.g., +91-6789234595) or original if no country code
 */
export const formatPhoneNumberWithDash = (phoneNumber) => {
  if (!phoneNumber || typeof phoneNumber !== 'string') {
    return phoneNumber || 'N/A';
  }

  // Remove any existing formatting
  const cleaned = phoneNumber.trim();
  
  // If no phone number, return N/A
  if (!cleaned || cleaned === 'N/A') {
    return 'N/A';
  }

  // If doesn't start with +, return as-is (old records without country code)
  if (!cleaned.startsWith('+')) {
    return cleaned;
  }

  // Remove the + sign for processing
  const withoutPlus = cleaned.substring(1);
  
  // If empty after removing +, return original
  if (!withoutPlus || !/^\d+$/.test(withoutPlus)) {
    return cleaned;
  }

  // Smart detection: Try to identify country code based on common patterns
  // Country codes can be 1, 2, or 3 digits
  // We'll try to detect based on total length and common patterns
  
  // Pattern 1: Single digit country code (US/Canada: +1)
  // Format: +1XXXXXXXXXX (11 digits total after +)
  if (withoutPlus.length === 11 && withoutPlus.startsWith('1')) {
    return `+1-${withoutPlus.substring(1)}`;
  }
  
  // Pattern 2: Two digit country codes (most common)
  // Examples: +91 (India), +44 (UK), +86 (China), etc.
  // Format: +XX followed by 7-15 digits
  if (withoutPlus.length >= 9 && withoutPlus.length <= 17) {
    // Try 2-digit code first (most common)
    const twoDigitCode = withoutPlus.substring(0, 2);
    const rest = withoutPlus.substring(2);
    
    // Common 2-digit codes: 20-99 (most countries)
    // If rest is 7-15 digits, it's likely a 2-digit country code
    if (rest.length >= 7 && rest.length <= 15 && /^\d+$/.test(rest)) {
      // Additional validation: Check if it's a known 2-digit code pattern
      const twoDigitCodes = ['20', '27', '30', '31', '32', '33', '34', '36', '39', '40', '41', '43', '44', '45', '46', '47', '48', '49', 
                            '51', '52', '53', '54', '55', '56', '57', '58', '60', '61', '62', '63', '64', '65', '66', 
                            '81', '82', '84', '86', '90', '91', '92', '93', '94', '95', '98'];
      
      // If it matches a known 2-digit code OR if the pattern looks right
      if (twoDigitCodes.includes(twoDigitCode) || (parseInt(twoDigitCode) >= 20 && parseInt(twoDigitCode) <= 99)) {
        return `+${twoDigitCode}-${rest}`;
      }
    }
  }
  
  // Pattern 3: Three digit country codes
  // Examples: +971 (UAE), +966 (Saudi), +212 (Morocco), etc.
  // Format: +XXX followed by 6-12 digits
  if (withoutPlus.length >= 9 && withoutPlus.length <= 18) {
    const threeDigitCode = withoutPlus.substring(0, 3);
    const rest = withoutPlus.substring(3);
    
    // If rest is 6-12 digits, it's likely a 3-digit country code
    if (rest.length >= 6 && rest.length <= 12 && /^\d+$/.test(rest)) {
      // Common 3-digit codes: 200-999
      const codeNum = parseInt(threeDigitCode);
      if (codeNum >= 200 && codeNum <= 999) {
        return `+${threeDigitCode}-${rest}`;
      }
    }
  }
  
  // Pattern 4: Single digit country code (other than +1)
  // Examples: +7 (Russia), etc.
  if (withoutPlus.length >= 8 && withoutPlus.length <= 16) {
    const oneDigitCode = withoutPlus.substring(0, 1);
    const rest = withoutPlus.substring(1);
    
    // If first digit is 7 (Russia) and rest is 10 digits
    if (oneDigitCode === '7' && rest.length === 10 && /^\d+$/.test(rest)) {
      return `+7-${rest}`;
    }
  }

  // Fallback: If we can't determine with certainty, use intelligent splitting
  // Try to split at a reasonable point based on total length
  if (withoutPlus.length >= 10) {
    // For numbers 10+ digits, try common splits
    // Most common: 2-digit code (90% of cases)
    if (withoutPlus.length >= 9 && withoutPlus.length <= 15) {
      // Assume 2-digit code for most cases
      const twoDigitCode = withoutPlus.substring(0, 2);
      const rest = withoutPlus.substring(2);
      if (rest.length >= 7) {
        return `+${twoDigitCode}-${rest}`;
      }
    }
  }

  // Final fallback: If we still can't format, return original (for old records)
  return cleaned;
};

/**
 * Format phone number for display (with fallback for old records)
 * @param {string} phoneNumber - Phone number
 * @returns {string} - Formatted phone number or original if formatting fails
 */
export const formatPhoneForDisplay = (phoneNumber) => {
  try {
    return formatPhoneNumberWithDash(phoneNumber);
  } catch (error) {
    // If formatting fails, return original (for old records)
    return phoneNumber || 'N/A';
  }
};

